import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { EditorSelectionContent } from "@/components/environments/EditorSelectionContent";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainEditor } from "@/queries/editor-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { OpenInEditorKeyBinding } from "@/utils/platform";
import { useRef, type FC, type ReactElement } from "react";

type OpenInEditorButtonProps = {
    environment: PlainEnvironment;
    disabled?: boolean;
};

export const OpenInEditorButton: FC<OpenInEditorButtonProps> = ({ environment, disabled }) => {
    const openEditor = useOpenEditor();
    const {
        selectedEditor,
        selectedEditorVersion,
        isSingleEditor,
        availableEditors,
        recommendedEditors,
        isPending: isPendingEditors,
    } = useGetEditorForEnv(environment.id, environment.metadata?.projectId);
    const anchor = useRef<HTMLDivElement>(null);

    // Get the latest version for the selected editor from the server-filtered versions
    // Server returns versions sorted with latest first
    const getLatestAllowedVersion = (editor: PlainEditor): string | undefined => {
        return editor.versions?.[0]?.version;
    };

    useOpenEditorShortcut((triggered) => {
        if (triggered && selectedEditor) {
            void openEditor(
                selectedEditor,
                environment,
                selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
            );
        }
    });

    const tooltip: string | ReactElement = disabled ? (
        "Start environment to open in editor"
    ) : (
        <div className="flex flex-row items-center gap-2">
            Open editor
            <KeyboardCombo keys={OpenInEditorKeyBinding} className="ml-1" />
        </div>
    );

    return (
        <div data-testid="open-in-default-editor" ref={anchor}>
            <SkeletonBlock ready={!!selectedEditor && !isPendingEditors}>
                {selectedEditor && (
                    <ButtonGroup variant="secondary">
                        <Tooltip content={tooltip} usePortal={true}>
                            <ButtonGroupItem
                                onClick={() =>
                                    openEditor(
                                        selectedEditor,
                                        environment,
                                        selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
                                    )
                                }
                                disabled={disabled}
                                LeadingIcon={(_props) => (
                                    <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />
                                )}
                                data-tracking-id="open-in-selected-editor"
                            >
                                <span>
                                    Open <span className="hidden @xs:inline">{selectedEditor.name}</span>
                                </span>
                            </ButtonGroupItem>
                        </Tooltip>
                        {!isSingleEditor && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <ButtonGroupItem
                                        data-testid="open-editor-selection-popover"
                                        LeadingIcon={IconChevronDownSmall}
                                        aria-label="Expand editor selection"
                                        disabled={disabled}
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-auto min-w-64 transition-all duration-200"
                                        data-track-location={TrackLocations.EnvironmentEditorPopover}
                                    >
                                        <EditorSelectionContent
                                            environment={environment}
                                            editors={availableEditors || []}
                                            recommendedEditors={recommendedEditors}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenuPortal>
                            </DropdownMenu>
                        )}
                    </ButtonGroup>
                )}
            </SkeletonBlock>
        </div>
    );
};
